import React, { useEffect } from 'react';
import { BrowserRouter, Route, Routes, useLocation } from "react-router-dom";
import PrivateRoute from './routers/privateRoute';
import { RouterConstant } from './routers/index';
import ChooseExercise from './pages/choose-exercise';
import DisplayVideo from './pages/display-video';
import ChooseExerciseWithHistory from './pages/choose-exercise-with-history';
import SearchExercise from './pages/search-exercise';
import CaptureExercise from './pages/capture-exercise';
import DisplayExercise from './pages/display-exercise';
import ExerciseQuestions from './pages/exercise-questions';
import QuestionSteps from './pages/question-steps';
import LOGIN from './pages/login';
import StartingPage1 from './pages/startingpage1';
import UploadComplete from './pages/upload-complete';
import PlayGroundPage from './pages/playground';
import CHAT from './pages/chat';
import SearchByBook from './pages/search-by-book';
import UserRegisterInitial from './pages/user-registration-initial';
import UserRegisterStep1 from './pages/user-registration-step1';
import UserRegisterOTP from './pages/user-registration-otp';
import UserProfile from './pages/user-profile';
import ResetPassword from './pages/reset-password';
import ErrorPage from './pages/error';
import MainLayout from './layouts/general-layout';
import MainDesktopLayout from './layouts/desktop-layout/desktop-layout';
import NewExercisesPage from './pages/admin/new-exercises';
import SelfTestStart from './pages/self-test-start';
import SelfTestExercises from './pages/self-test-exercises';
import SelfTestSummary from './pages/self-test-summary';
import SelfTestAnswerPreview from './pages/self-test-answer-preview';
import { ToastContainer } from 'react-toastify';
import "react-toastify/dist/ReactToastify.css";
import useAudioStore from './utils/audioStore';
import { excludeTrackingPages } from "../src/utils/utils";
import AdminProtectedRoute from './routers/adminProtectedRoute';
import TeacherProtectedRoute from './routers/teacherProtectedRoute';

const TeacherLayout = React.lazy(() => import('./layouts/teacher-layout'));
const TeacherDashboardOverview = React.lazy(() => import('./pages/teacher-dashboard/overview'));
const TeacherTopicsPage = React.lazy(() => import('./pages/teacher-dashboard/topics'));
const TeacherPracticesPage = React.lazy(() => import('./pages/teacher-dashboard/practices'));
const TeacherDashboardAccount = React.lazy(() => import('./pages/teacher-dashboard/account'));


import { AudioService } from "./services/text-to-audio/texttoaudio.service";
import { TrackingService } from "./services/tracking/tracking.service";
import { ChatService } from './services/chat/chat.services';
import ChoosePackages from './pages/choose-packages';
import SubscriptionExpiredModal from './components/subscription-expired-modal';
import Payment from './pages/payment';
import PaymentStatus from './pages/payment/payment-status';
import SharedLesson from './pages/shared-lesson';
import V2Onboarding from './pages/v2/onboarding';
import Learning from './pages/learning';
import UserMainDashboard from './pages/v2/user-main-dashboard';
import SearchLesson from './pages/search-lesson';
import PlayLesson from './pages/play-lesson';
import LessonSummary from './pages/lesson-summary';
import UserAllMissions from './pages/v2/user-all-missions';
import ExercisePractice from './pages/exercise-practice';
import ExercisesHomework from './pages/exercises-homework';
import NotificationCenter from './pages/notifications';


// Route change listener component
const RouteChangeListener = () => {
  const location = useLocation();
  const trackingServiceInstance = new TrackingService();
  const { ROUTERS_PATH } = RouterConstant;
  const stopAudio = useAudioStore(state => state.stopAudio);
  
  useEffect(() => {
    AudioService.stopAudio();
    stopAudio();

    const routeName = Object.keys(ROUTERS_PATH).find(
      (key) => ROUTERS_PATH[key as keyof typeof ROUTERS_PATH] === location.pathname
    ) as keyof typeof ROUTERS_PATH;

    AudioService.setCurrentPage(routeName || "");

    if (!routeName || excludeTrackingPages.includes(location.pathname)) return;

    trackingServiceInstance.trackPageView(
      routeName,
      window.location.href,
      null,
      { source: `${routeName}-Page` }
    );

    // Log route activity transition to FastAPI Context Ticket
    const handleContextActivityPing = async () => {
      let pageKey = 'unknown';
      let meta: any = {};

      if (location.pathname === ROUTERS_PATH.USER_MAIN_DASHBOARD) {
        pageKey = 'dashboard';
      } else if (location.pathname === ROUTERS_PATH.PLAY_LESSON) {
        pageKey = 'learning';
        const state = location.state as any;
        if (state?.lessonId) {
          meta.lesson_id = state.lessonId;
        }
      } else if (location.pathname === ROUTERS_PATH.EXERCISE_PRACTICE) {
        pageKey = 'practice';
        const state = location.state as any;
        if (state?.exerciseId) {
          meta.exercise_id = state.exerciseId;
        }
        if (state?.lessonId) {
          meta.lesson_id = state.lessonId;
        }
      } else if (location.pathname === ROUTERS_PATH.EXERCISES_HOMEWORK) {
        pageKey = 'exercises_homework';
      }

      if (pageKey !== 'unknown') {
        try {
          const chatService = new ChatService();
          await chatService.sendActivity({
            page: pageKey,
            metadata: meta
          });
        } catch (err) {
          console.error("Failed to sync context activity ping:", err);
        }
      }
    };

    handleContextActivityPing();
  }, [location]);

  useEffect(() => {
    const handleUserClick = () => {
      if (AudioService.isPlaying()) {
        AudioService.pauseAudio();
      }
    };
  
    document.addEventListener("click", handleUserClick);
  
    return () => {
      document.removeEventListener("click", handleUserClick);
    };
  }, []);
  
  return null;
};

const App = () => {
  const { ROUTERS_PATH } = RouterConstant;

  return (
    <BrowserRouter>
      <RouteChangeListener />
      {/* Check if the current path is playground */}
      <Routes>
        {/* Playground - standalone layout */}
        <Route path={ROUTERS_PATH.PLAYGROUND} element={<PlayGroundPage />} />
        <Route path={ROUTERS_PATH.SHAREDLESSON} element={<SharedLesson />} />
        {/* Dashboard routes */}
        <Route element={<AdminProtectedRoute><MainDesktopLayout /></AdminProtectedRoute>}>
          <Route path={ROUTERS_PATH.DASHBOARD} element={<NewExercisesPage />} />
        </Route>

        {/* Teacher Dashboard routes */}
        <Route element={<TeacherProtectedRoute><React.Suspense fallback={<div>Loading...</div>}><TeacherLayout /></React.Suspense></TeacherProtectedRoute>}>
          <Route path={ROUTERS_PATH.TEACHER_DASHBOARD} element={<TeacherDashboardOverview />} />
          <Route path={ROUTERS_PATH.TEACHER_TOPICS} element={<TeacherTopicsPage />} />
          <Route path={ROUTERS_PATH.TEACHER_PRACTICES} element={<TeacherPracticesPage />} />
          <Route path={ROUTERS_PATH.TEACHER_ACCOUNT} element={<TeacherDashboardAccount />} />
        </Route>

        {/* Main layout */}
        <Route element={<MainLayout />}>
          {/* Public Routes */}
          <Route path={ROUTERS_PATH.ROOT_PATH} element={<StartingPage1 />} />
          <Route path={ROUTERS_PATH.STARTINGPAGE1} element={<StartingPage1 />} />
          <Route path={ROUTERS_PATH.LOGIN} element={<LOGIN />} />
          <Route path={ROUTERS_PATH.UPLOADCOMPLETE} element={<UploadComplete />} />
          <Route path={ROUTERS_PATH.CAPTUREEXERCISE} element={<CaptureExercise />} />
          <Route path={ROUTERS_PATH.USERREGISTRATIONINITIAL} element={<UserRegisterInitial />} />
          <Route path={ROUTERS_PATH.USERREGISTRATIONSTEP1} element={<UserRegisterStep1 />} />
          <Route path={ROUTERS_PATH.USERREGISTRATIONOTP} element={<UserRegisterOTP />} />
          <Route path={ROUTERS_PATH.RESETPASSWORD} element={<ResetPassword />} />          

          {/* Protected Routes */}
          <Route element={<PrivateRoute />}>
            <Route path={ROUTERS_PATH.V2_ONBOARDING} element={<V2Onboarding />} />
            <Route path={ROUTERS_PATH.DISPLAYVIDEO} element={<DisplayVideo />} />
            <Route path={ROUTERS_PATH.CHOOSEEXERCISE} element={<ChooseExercise />} />
            <Route path={ROUTERS_PATH.CHOOSEEXERCISEWITHHISTORY} element={<ChooseExerciseWithHistory />} />
            <Route path={ROUTERS_PATH.SEARCHEXRCISE} element={<SearchExercise />} />
            <Route path={ROUTERS_PATH.DISPLAYEXERCISE} element={<DisplayExercise />} />
            <Route path={ROUTERS_PATH.EXERCISEQUESTIONS} element={<ExerciseQuestions />} />
            <Route path={ROUTERS_PATH.QUESTIONSTEPS} element={<QuestionSteps />} />
            <Route path={ROUTERS_PATH.CHAT} element={<CHAT />} />
            <Route path={ROUTERS_PATH.SEARCHBYBOOK} element={<SearchByBook />} />
            <Route path={ROUTERS_PATH.USERPROFILE} element={<UserProfile />} />
            <Route path={ROUTERS_PATH.SELFTESTSTART} element={<SelfTestStart />} />
            <Route path={ROUTERS_PATH.SELFTESTEXERCISES} element={<SelfTestExercises />} />
            <Route path={ROUTERS_PATH.SELFTESTSUMMARY} element={<SelfTestSummary />} />
            <Route path={ROUTERS_PATH.SELFTESTANSWERPREVIEW} element={<SelfTestAnswerPreview />} />
            <Route path={ROUTERS_PATH.CHOOSEPACKAGES} element={<ChoosePackages />} />            
            <Route path={ROUTERS_PATH.PAYMENT} element={<Payment />} />
            <Route path={ROUTERS_PATH.PAYMENTSTATUS} element={<PaymentStatus />} />
            <Route path={ROUTERS_PATH.LEARNING} element={<Learning />} />
            <Route path={ROUTERS_PATH.USER_MAIN_DASHBOARD} element={<UserMainDashboard />} />
            <Route path={ROUTERS_PATH.SEARCH_LESSON} element={<SearchLesson />} />
            <Route path={ROUTERS_PATH.PLAY_LESSON} element={<PlayLesson />} />
            <Route path={ROUTERS_PATH.LESSON_SUMMARY} element={<LessonSummary />} />
            <Route path={ROUTERS_PATH.USER_ALL_MISSIONS} element={<UserAllMissions />} />
            <Route path={ROUTERS_PATH.EXERCISE_PRACTICE} element={<ExercisePractice />} />
            <Route path={ROUTERS_PATH.EXERCISES_HOMEWORK} element={<ExercisesHomework />} />
            <Route path={ROUTERS_PATH.NOTIFICATIONS} element={<NotificationCenter />} />

          </Route>

          {/* Catch-all error route */}
          <Route path="*" element={<ErrorPage />} />
        </Route>
      </Routes>

      <ToastContainer />
      <SubscriptionExpiredModal />
    </BrowserRouter>
  );
};

export default App;
